// Global Instructions Rule Applied!

import React, { useRef, useCallback, useEffect } from 'react'
import { useDragLayer } from 'react-dnd'
import { AutoScrollMonitorProps } from '../../interfaces'
import {
  AUTO_SCROLL_TICK_MS,
  KANBAN_COLUMN_SCROLL_CLASS,
  edgeScrollStep
} from '../../controller/parts/autoScrollUtils.ts'

interface ScrollTargets {
  /** The board's horizontal scroll container and its step per tick */
  board: HTMLElement | null
  stepX: number
  /** The column card container under the pointer and its step per tick */
  column: HTMLElement | null
  stepY: number
}

const IDLE: ScrollTargets = { board: null, stepX: 0, column: null, stepY: 0 }

/**
 * Scrolls while a card is dragged near an edge: the board sideways when the
 * pointer nears its left/right edge, and the column under the pointer up or
 * down when the pointer nears its top/bottom edge. Drop zones keep tracking
 * the pointer as the content moves underneath it.
 */
export const AutoScrollMonitor: React.FC<AutoScrollMonitorProps> = ({ scrollContainerRef }) => {
  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const targetsRef = useRef<ScrollTargets>(IDLE)

  const { isDragging, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    currentOffset: monitor.getClientOffset()
  }))

  const startAutoScroll = useCallback((): void => {
    if (scrollIntervalRef.current) return
    scrollIntervalRef.current = setInterval(() => {
      const { board, stepX, column, stepY } = targetsRef.current
      if (board && stepX) board.scrollLeft += stepX
      if (column && stepY) column.scrollTop += stepY
    }, AUTO_SCROLL_TICK_MS)
  }, [])

  const stopAutoScroll = useCallback((): void => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
      scrollIntervalRef.current = null
    }
    targetsRef.current = IDLE
    // Remove visual feedback classes
    document.body.classList.remove('auto-scroll-left', 'auto-scroll-right')
  }, [])

  useEffect(() => {
    const board = scrollContainerRef.current
    if (!isDragging || !currentOffset || !board) {
      stopAutoScroll()
      return
    }

    const boardRect = board.getBoundingClientRect()
    const stepX = edgeScrollStep(
      currentOffset.x,
      boardRect.left,
      boardRect.right,
      board.scrollLeft,
      board.scrollWidth - board.clientWidth
    )

    const column =
      document
        .elementFromPoint(currentOffset.x, currentOffset.y)
        ?.closest<HTMLElement>(`.${KANBAN_COLUMN_SCROLL_CLASS}`) ?? null
    let stepY = 0
    if (column) {
      const columnRect = column.getBoundingClientRect()
      stepY = edgeScrollStep(
        currentOffset.y,
        columnRect.top,
        columnRect.bottom,
        column.scrollTop,
        column.scrollHeight - column.clientHeight
      )
    }

    // Visual feedback class on the document body for the sideways scroll
    document.body.classList.toggle('auto-scroll-left', stepX < 0)
    document.body.classList.toggle('auto-scroll-right', stepX > 0)

    if (stepX || stepY) {
      targetsRef.current = { board, stepX, column, stepY }
      startAutoScroll()
    } else {
      stopAutoScroll()
    }
  }, [isDragging, currentOffset, scrollContainerRef, startAutoScroll, stopAutoScroll])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAutoScroll()
    }
  }, [stopAutoScroll])

  return null // This component doesn't render anything
}
